// ─────────────────────────────────────────────
//  notesLocums — parse the sheet's NOTES row for locum / temporary coverage
//  ("Name/Code - Shift Type", comma-separated). Parsing stops after the LUT
//  entry (LUT - Night Float); anything beyond it ("Post Nights", …) is ignored
//  by design.
//
//  ⚠️ THE ROW IS FOUND BY ITS CONTENT, NOT BY AN OFFSET. This used to read the
//  row IMMEDIATELY BELOW the "Marker row for importer" line. On 2026-08-20
//  somebody inserted a `VAC:` row between the marker and NOTES:
//
//      row 61  ----------------      Marker row for importer.  No changes.
//      row 62  VAC: CAR, HAW, HUA, …          ← what it read
//      row 63  NOTES: Villanueva/McDonald- Day Hawk, …   ← the real row
//
//  and `parseNotesLocums` returned []. Today's Day Hawk (11:00–23:00) and
//  Night Float (18:00–07:00) cover was simply absent from the roster — no
//  error, no flag, no log line, and the staffing-adequacy maths quietly lost
//  its evening and overnight supervisors. The marker still anchors the SEARCH;
//  the NOTES: text now decides the row.
//
//  ⚠️ AND A SLASH IS NOT ALWAYS "name/code". `Luttman/LUT` is one doctor and
//  his short code; `Villanueva/McDonald` is TWO doctors, and reading it the
//  first way emitted a single chimera named "Villanueva" carrying code
//  "MCDONALD" — a person who does not exist, covering a shift two people are
//  actually working. The discriminator is shape: a short ALL-CAPS token is a
//  code, a mixed-case token is a surname.
//
//  Fixed shift interpretations (times are locked, not read from the sheet):
//    Day Hawk    → 11:00 → 23:00 (same day)
//    Night Float → 18:00 → 07:00 NEXT day
//
//  Pure — no Firebase, no React.
// ─────────────────────────────────────────────

const MARKER_RE = /marker row for importer/i;
const NOTES_RE = /^\s*notes?\s*[:\-]/i;
// How far below the marker to look for the NOTES row. The department inserts
// rows here (VAC:, blanks); beyond a handful of lines we are guessing.
const NOTES_SEARCH_DEPTH = 8;

// ⚠️ `facilityId` SCOPES A COVER SHIFT TO ONE CAMPUS, and its absence means
// "every campus". One sheet serves all three campuses and the NOTES row names no
// campus at all — today's reads `OSU - Night Float` — so before this the
// importer had nothing to scope by and wrote the overnight doctor onto ALL
// THREE boards. On 2026-08-24 Babajide (Michael) Osunsanmi was a supervisor at
// Wesley Chapel, Carrollwood AND Tampa simultaneously, holding no room at any of
// them. One person cannot be at three hospitals.
//
// Owner, 2026-08-24: *"Night float is for tampa, and go fro 6:30 pm to 7:00 am"*.
// Day Hawk is left unscoped because nobody has said which campus it belongs to —
// guessing would repeat the bug in the other direction.
const SHIFTS = {
  "day hawk": { kind: "Day Hawk", coverStart: "11:00", coverEnd: "23:00", coverEndNextDay: false },
  "night float": { kind: "Night Float", coverStart: "18:30", coverEnd: "07:00", coverEndNextDay: true, facilityId: "tampa" },
};

const normName = (n) => String(n || "").replace(/^dr\.?\s+/i, "").trim().toLowerCase();
const rowText = (row) => (row || []).map((c) => String(c ?? "").trim()).filter(Boolean).join(" ");

// A short ALL-CAPS run is a doctor's short code ("LUT", "MAH"); anything with a
// lower-case letter is a surname ("McDonald", "Villanueva").
const isShortCode = (token) => /^[A-Z]{2,5}$/.test(String(token || "").trim());

/**
 * Find the NOTES row: anchored on the importer marker, located by its own text.
 * Falls back to the row directly below the marker only when nothing in the
 * window says "NOTES:", so an unlabelled row still parses as it used to.
 */
export function findNotesRow(values) {
  const rows = Array.isArray(values) ? values : [];
  const idx = rows.findIndex((r) => (r || []).some((c) => MARKER_RE.test(String(c ?? ""))));
  if (idx < 0) return -1;
  const limit = Math.min(rows.length, idx + 1 + NOTES_SEARCH_DEPTH);
  for (let i = idx + 1; i < limit; i += 1) {
    if (NOTES_RE.test(rowText(rows[i]))) return i;
  }
  return idx + 1 < rows.length ? idx + 1 : -1;
}

/**
 * One "Villanueva/McDonald" or "Luttman/LUT" or "KUJ/MAH/WEB" token → the
 * people it names, as { code, displayName } pairs.
 */
function peopleInToken(token) {
  const parts = String(token || "").split("/").map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) {
    const only = parts[0] || "";
    return only ? [{ code: only.toUpperCase(), displayName: only }] : [];
  }
  const names = parts.filter((p) => !isShortCode(p));
  const codes = parts.filter(isShortCode);
  // "Luttman/LUT" — one surname and its code.
  if (names.length === 1 && codes.length >= 1) {
    return [{ code: codes[codes.length - 1].toUpperCase(), displayName: names[0] }];
  }
  // "Villanueva/McDonald" — two surnames. "KUJ/MAH/WEB" — three codes.
  // Either way each part is its own person; a chimera is never emitted.
  return parts.map((p) => ({ code: p.toUpperCase(), displayName: p }));
}

export function parseNotesLocums(values, { doctors = [] } = {}) {
  const rows = Array.isArray(values) ? values : [];
  const idx = findNotesRow(rows);
  if (idx < 0 || idx >= rows.length) return [];
  const text = rowText(rows[idx]);
  if (!text) return [];
  const body = text.replace(/^notes?\s*[:\-]?\s*/i, "");

  const out = [];
  for (const partRaw of body.split(",")) {
    const part = partRaw.trim();
    if (!part) continue;
    // "Name/Code - Shift Type" — the token before the dash.
    const m = part.match(/^([^-–—]+?)\s*[-–—]\s*(.+)$/);
    const token = (m ? m[1] : part).trim();
    const people = peopleInToken(token);
    const isLut = people.some((p) => p.code === "LUT");

    const shiftKey = m
      ? Object.keys(SHIFTS).find((k) => new RegExp(`^${k.replace(" ", "\\s*")}\\b`, "i").test(m[2].trim()))
      : null;
    if (shiftKey) {
      const spec = SHIFTS[shiftKey];
      for (const person of people) {
        const st =
          doctors.find((d) => String(d.shortCode || "").toUpperCase() === person.code) ||
          doctors.find((d) => normName(d.name) === normName(person.displayName));
        out.push({
          code: person.code,
          id: st?.id || null,
          name: st?.name || person.displayName,
          ...spec,
        });
      }
    }
    // LUT is the LAST entry we process — stop, ignore "Post Nights" etc.
    if (isLut) break;
  }
  return out;
}
